import { type User } from '@auth0/auth0-react';
import LogRocket from 'logrocket';

import { APP_VERSION } from 'utils/env';
import { getHeap } from 'utils/heap';

const { NODE_ENV, DEPLOY_ENV } = process.env;

// This needs to match the App ID (org + project name) specified in LogRocket
const HORIZON_LOGROCKET_NAME = 'j1veqt/horizon-prod-izxnz';

const isProd = () => DEPLOY_ENV === 'prod' && NODE_ENV === 'production';

// LogRocket only runs in Prod for now.
const addHorizonLogRocket = () => isProd();

// Urls we don't want to capture in LogRocket
const restrictedUrls = ['https://sessions.bugsnag.com', 'https://heapanalytics.com'];

const horizonLogRocketOptions = (): LR.IOptions => ({
  dom: {
    // https://docs.logrocket.com/reference/dom
    // NOTE: images are individually hidden with a data-private attribute
    inputSanitizer: true,
    textSanitizer: true,
  },
  network: {
    requestSanitizer: (request) => {
      // Exclude requests to  restricted urls
      if (restrictedUrls.some((url) => request.url.includes(url))) {
        return null;
      }

      // redact Authorization headers from requests
      if (request.headers['Authorization']) {
        request.headers['Authorization'] = '<redacted>';
      }
      if (request.headers['Bugsnag-Api-Key']) {
        request.headers['Bugsnag-Api-Key'] = '<redacted>';
      }

      return request;
    },
    responseSanitizer: (response) => {
      // remove body from token responses
      if (response.body?.includes('access_token') || response.body?.includes('refresh_token')) {
        delete response.body;
      }
      return response;
    },
  },
  // https://docs.logrocket.com/reference/capture-message
  console: {
    shouldAggregateConsoleErrors: true,
  },
  // https://docs.logrocket.com/reference/release
  release: APP_VERSION,
});

const setLogRocketUser = ({ email, sub, ...rest }: User) => {
  // If we don't have the user's sub or email don't try init user
  if (!sub || !email) {
    return;
  }

  // Add identifying information.
  LogRocket.identify(sub, {
    email,
    skySpecsGroups: rest['https://skyspecs.com/jwt/ohi'].groups,
  });

  // Add LogRocket session URL to Heap.
  if (getHeap()) {
    LogRocket.getSessionURL(function (sessionURL) {
      getHeap().addUserProperties('LogRocket', { sessionURL: sessionURL });
    });
  }
};

// Initialize LogRocket
if (addHorizonLogRocket()) {
  LogRocket.init(HORIZON_LOGROCKET_NAME, horizonLogRocketOptions());
}

export { setLogRocketUser };
